/**
 * Laboratory module API — lab order lifecycle + technician queue.
 *
 * Backend (all under the authenticated user's organisation):
 *   POST   /api/organisation/lab/orders                              (advise)
 *   POST   /api/organisation/lab/orders/:id/assign
 *   POST   /api/organisation/lab/orders/:id/collect-specimen
 *   POST   /api/organisation/lab/orders/:id/start
 *   POST   /api/organisation/lab/orders/:id/complete
 *   POST   /api/organisation/lab/orders/:id/cancel
 *   GET    /api/organisation/lab/orders/assigned?status_prefix=       (my queue)
 *   GET    /api/organisation/lab/orders?visit_id=                     (per visit)
 *   GET    /api/organisation/lab/orders/:id
 *
 * Every function returns `{ success, data?, error? }`.
 */
import apiClient from './apiClient';

function buildApiError(error, fallback) {
  const data = error?.response?.data;
  const detail = data?.detail;
  // Structured error envelope `{ detail: { code, message } }` (e.g. the IPD
  // advise 422 IPD_LAB_CREDIT_ONLY) — surface the human `message`, never the
  // raw JSON blob. Falls through to the status map when neither is present.
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    if (detail.message) return detail.message;
  }
  const serverMsg = detail ?? data?.message;
  if (serverMsg) return typeof serverMsg === 'string' ? serverMsg : JSON.stringify(serverMsg);
  switch (error?.response?.status) {
    case 401: return 'Session expired. Please login again.';
    case 403: return 'You do not have permission to perform this action.';
    case 404: return 'The requested lab order could not be found.';
    case 409: return 'This lab order has already moved on — refresh and try again.';
    case 422: return 'Invalid data — please check and try again.';
    case 500: return 'Server error. Please try again in a moment.';
    default:  return fallback;
  }
}

const BASE = '/api/organisation/lab/orders';
const enc = encodeURIComponent;

export const adviseLabOrder = async (body) => {
  try {
    const res = await apiClient.post(BASE, body);
    return { success: true, data: res.data };
  } catch (error) {
    console.error('[LabAPI] advise error:', error);
    // Surface the HTTP status + structured code alongside the human message so
    // the caller can distinguish a permission/403 failure (retry never helps)
    // from a transient one (retry might).
    return {
      success: false,
      error: buildApiError(error, 'Failed to add lab test.'),
      status: error?.response?.status,
      code: error?.response?.data?.detail?.code,
    };
  }
};

const _action = (name, path, fallback) => async (id, body) => {
  try {
    const res = await apiClient.post(`${BASE}/${enc(id)}/${path}`, body || {});
    return { success: true, data: res.data };
  } catch (error) {
    console.error(`[LabAPI] ${name} error:`, error);
    // `code` rides alongside the human message so a caller that owns its own
    // ratified copy (e.g. DiagnosticsCard's cancel refusal) can key off the
    // machine code instead of rendering server prose.
    return { success: false, error: buildApiError(error, fallback), code: error?.response?.data?.detail?.code };
  }
};

export const assignLabOrder = _action('assign', 'assign', 'Failed to assign lab order.');
export const collectSpecimen = _action('collectSpecimen', 'collect-specimen', 'Failed to record specimen.');
export const rejectSample = _action('rejectSample', 'reject-sample', 'Failed to reject the sample.');
export const startLabOrder = _action('start', 'start', 'Failed to start the test.');
export const completeLabOrder = _action('complete', 'complete', 'Failed to complete the test.');
export const cancelLabOrder = _action('cancel', 'cancel', 'Failed to cancel the lab order.');
// Standing orders: perform again (clone a child order) / renew the review date.
// body: perform → { visit_id? }; reviewDate → { expires_at }.
export const performLabOrder = _action('perform', 'perform', 'Failed to perform the lab test again.');
export const updateLabReviewDate = _action('reviewDate', 'review-date', 'Failed to update the review date.');

export const listMyLabQueue = async (statusPrefix) => {
  try {
    const params = statusPrefix ? { status_prefix: statusPrefix } : {};
    const res = await apiClient.get(`${BASE}/assigned`, { params });
    return { success: true, data: res.data?.lab_orders || [] };
  } catch (error) {
    console.error('[LabAPI] my queue error:', error);
    return { success: false, error: buildApiError(error, 'Failed to load the lab queue.') };
  }
};

export const listLabOrdersForVisit = async (
  visitId,
  { includeAdvised = false, includeCompleted = false } = {},
) => {
  try {
    const params = { visit_id: visitId };
    // Doctor's per-visit summary also wants orders ADVISED here but since carried
    // onto their own lab visit (assigned → … → completed) — see backend include_advised.
    if (includeAdvised) params.include_advised = true;
    // Hybrid scope: also the patient's recent COMPLETED labs from other visits (review).
    if (includeCompleted) params.include_completed = true;
    const res = await apiClient.get(BASE, { params });
    // admission-access C-3 — `money_visible` is the list-level flag (false = the
    // server nulled every money field on these rows). Passed through untouched:
    // `undefined` (an older server) means "no change", so only a surface that
    // renders money reads it; `data` stays the plain orders array for everyone else.
    return { success: true, data: res.data?.lab_orders || [], money_visible: res.data?.money_visible };
  } catch (error) {
    console.error('[LabAPI] visit list error:', error);
    // `code` rides along (as on the action calls below) so a caller can key off
    // the machine code — e.g. the admission department wall's OUT_OF_DEPARTMENT_SCOPE.
    return { success: false, error: buildApiError(error, 'Failed to load lab orders.'), code: error?.response?.data?.detail?.code };
  }
};

export const getLabOrder = async (id) => {
  try {
    const res = await apiClient.get(`${BASE}/${enc(id)}`);
    return { success: true, data: res.data };
  } catch (error) {
    console.error('[LabAPI] get error:', error);
    return { success: false, error: buildApiError(error, 'Failed to load lab order.') };
  }
};

// Doctor's patient-scoped Lab summary — the patient's RECENT labs across visits
// in ANY status (advised / completed / expired / cancelled), newest-first +
// bounded. Returns { data: orders, hasMore } so the UI can link to full history.
export const listRecentLabOrdersForPatient = async (patientId, { limit = 25 } = {}) => {
  try {
    const res = await apiClient.get('/api/organisation/lab/orders/patient-recent', {
      params: { patient_id: patientId, limit },
    });
    return { success: true, data: res.data?.lab_orders || [], hasMore: !!res.data?.has_more };
  } catch (error) {
    console.error('[LabAPI] patient-recent error:', error);
    return { success: false, error: buildApiError(error, 'Failed to load lab history.') };
  }
};

// Registration handoff: a patient's OPEN (non-terminal) lab orders — the
// advised-and-unassigned ones a receptionist assigns, plus any in-flight.
export const listOpenLabItems = async (patientId) => {
  try {
    // open-items is a sibling of /lab/orders (i.e. /lab/open-items), NOT nested
    // under BASE — using `${BASE}/open-items` hits /lab/orders/open-items → 404.
    const res = await apiClient.get('/api/organisation/lab/open-items', { params: { patient_id: patientId } });
    return { success: true, data: res.data?.lab_orders || [] };
  } catch (error) {
    console.error('[LabAPI] open-items error:', error);
    return { success: false, error: buildApiError(error, 'Failed to load open lab items.') };
  }
};

// ── Billing (P5) ─────────────────────────────────────────────────────
// Collect the in-house lab fee (canonical Payment + GST receipt). Returns the
// stamped order. A done-outside (external) lab is rejected server-side.
export const collectLabPayment = async (labOrderId, paymentMode) => {
  try {
    const res = await apiClient.post(`${BASE}/${enc(labOrderId)}/collect-payment`, {
      payment_mode: paymentMode || 'CASH',
    });
    return { success: true, data: res.data };
  } catch (error) {
    return { success: false, error: buildApiError(error, 'Failed to collect payment.') };
  }
};

// Collect several of a visit's lab fees on ONE shared receipt ("collected
// together = one receipt"). Returns { lab_orders } (the updated orders).
export const collectLabPaymentBundle = async (labOrderIds, paymentMode) => {
  try {
    const res = await apiClient.post(`${BASE}/collect-payment-bundle`, {
      lab_order_ids: labOrderIds,
      payment_mode: paymentMode || 'CASH',
    });
    return { success: true, data: res.data?.lab_orders || [] };
  } catch (error) {
    return { success: false, error: buildApiError(error, 'Failed to collect lab payment.') };
  }
};

// ── Reports (P4) ─────────────────────────────────────────────────────
export const uploadLabReport = async (labOrderId, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const res = await apiClient.post(`${BASE}/${enc(labOrderId)}/reports`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return { success: true, data: res.data };
  } catch (error) {
    return { success: false, error: buildApiError(error, 'Failed to upload report.') };
  }
};

// Done-outside: attach the external report + complete the order (returns the order).
// Mark an order "Done (outside)". The done-outside state is the record, so BOTH
// the report file and the note are optional — attach a report if the patient
// brought one, else just a comment, else neither.
export const recordLabExternal = async (labOrderId, file = null, note = null) => {
  try {
    const formData = new FormData();
    if (file) formData.append('file', file);
    if (note) formData.append('note', note);
    const res = await apiClient.post(`${BASE}/${enc(labOrderId)}/record-external`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return { success: true, data: res.data };
  } catch (error) {
    return { success: false, error: buildApiError(error, 'Failed to record external result.') };
  }
};

export const listLabReports = async (labOrderId) => {
  try {
    const res = await apiClient.get(`${BASE}/${enc(labOrderId)}/reports`);
    return { success: true, data: res.data?.reports || [] };
  } catch (error) {
    return { success: false, error: buildApiError(error, 'Failed to load reports.') };
  }
};

// Replace a report — keeps the old (superseded) + uploads an amended one.
export const replaceLabReport = async (labOrderId, reportId, file, reason) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('replace_reason', reason);
    const res = await apiClient.put(`${BASE}/${enc(labOrderId)}/reports/${enc(reportId)}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return { success: true, data: res.data };
  } catch (error) {
    return { success: false, error: buildApiError(error, 'Failed to replace report.') };
  }
};

// Soft-delete a report (kept for audit with the reason).
export const deleteLabReport = async (labOrderId, reportId, reason) => {
  try {
    const res = await apiClient.delete(`${BASE}/${enc(labOrderId)}/reports/${enc(reportId)}`, {
      data: { reason },
    });
    return { success: true, data: res.data };
  } catch (error) {
    return { success: false, error: buildApiError(error, 'Failed to delete report.') };
  }
};

// Proxied download — returns an object-URL for the report bytes (no S3 URL).
export const downloadLabReport = async (labOrderId, reportId) => {
  try {
    const res = await apiClient.get(`${BASE}/${enc(labOrderId)}/reports/${enc(reportId)}/download`, {
      responseType: 'blob',
    });
    const contentType = res.headers['content-type'] || 'application/octet-stream';
    const blobUrl = URL.createObjectURL(new Blob([res.data], { type: contentType }));
    return { success: true, data: { blobUrl, contentType } };
  } catch (error) {
    return { success: false, error: buildApiError(error, 'Failed to download report.') };
  }
};
